import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useTheme } from '../contexts/ThemeContext'
import { PageContent } from '../components/Layout'
import { useAuth } from '../hooks/useAuth'
import { makeApiCall, makeJsonApiCall } from '../utils/customerUtils'
import { useViewSettings } from '../hooks/useViewSettings'
import FollowUpModal from '../components/FollowUpModal'
import LeadDetailModal from '../components/LeadDetailModal'
import LeadsTableView from '../components/LeadsTableView'

interface Lead {
  id: number
  first_name: string | null
  last_name: string | null
  email: string | null
  phone: string | null
  description: string | null
  status_id: number
  status_name: string
  status_color: string
  urgency_id: number
  urgency_name: string
  urgency_color: string
  priority: 'low' | 'medium' | 'high' | null
  best_time_to_contact: 'day' | 'evening' | 'weekend' | null
  task_count: number
  created_at: string
  creator_first_name: string
  creator_last_name: string
  assigned_to?: number
  assigned_to_name?: string
  assigned_at?: string
  partner_id?: number | null
  partner_name?: string | null
  partner_email?: string | null
}

interface LeadStatus {
  id: number
  name: string
  color: string
  position: number
}

interface UrgencyLevel {
  id: number
  name: string
  color: string
  position: number
}

interface DealStatus {
  id: number
  name: string
  color: string
}

interface DealType {
  id: number
  name: string
}

interface AddLeadModal {
  isOpen: boolean
  editingLead?: Lead | null
}

interface ConvertLeadModal {
  isOpen: boolean
  lead: Lead | null
}

interface FollowUpModalState {
  isOpen: boolean
  lead: Lead | null
}

interface LeadDetailModalState {
  isOpen: boolean
  lead: Lead | null
}

const Leads: React.FC = () => {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const { user } = useAuth()

  // View settings hook
  const { 
    settings: viewSettings, 
    loading: viewSettingsLoading, 
    toggleViewMode,
    setViewMode
  } = useViewSettings('leads_page')

  // State
  const [leads, setLeads] = useState<Lead[]>([])
  const [leadStatuses, setLeadStatuses] = useState<LeadStatus[]>([])
  const [urgencyLevels, setUrgencyLevels] = useState<UrgencyLevel[]>([])
  const [dealStatuses, setDealStatuses] = useState<DealStatus[]>([])
  const [dealTypes, setDealTypes] = useState<DealType[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  
  // Partner state
  const [partners, setPartners] = useState<any[]>([])
  const [partnerSearch, setPartnerSearch] = useState('')
  const [showPartnerDropdown, setShowPartnerDropdown] = useState(false)
  const [showAddPartnerModal, setShowAddPartnerModal] = useState(false)
  const [newPartnerForm, setNewPartnerForm] = useState({ first_name: '', last_name: '', email: '', phone: '', agency: '' })
  const partnerDropdownRef = useRef<HTMLDivElement>(null)

  // Close partner dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (partnerDropdownRef.current && !partnerDropdownRef.current.contains(e.target as Node)) {
        setShowPartnerDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Filters
  const [filters, setFilters] = useState({
    status_id: '',
    urgency_id: '',
    search: ''
  })

  // Modals
  const [addLeadModal, setAddLeadModal] = useState<AddLeadModal>({ isOpen: false })
  const [convertLeadModal, setConvertLeadModal] = useState<ConvertLeadModal>({ isOpen: false, lead: null })
  const [followUpModal, setFollowUpModal] = useState<FollowUpModalState>({ isOpen: false, lead: null })
  const [leadDetailModal, setLeadDetailModal] = useState<LeadDetailModalState>({ isOpen: false, lead: null })
  const [leadFormLoading, setLeadFormLoading] = useState(false)
  const [leadFormError, setLeadFormError] = useState<string | null>(null)
  const [showTaskSection, setShowTaskSection] = useState(false)

  // Form data
  const [leadForm, setLeadForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    description: '',
    status_id: '',
    urgency_id: '',
    partner_id: '',
    task: {
      description: '',
      due_date: '',
      priority: 'medium' as 'low' | 'medium' | 'high'
    }
  })

  const [convertForm, setConvertForm] = useState({
    missing_info: {
      first_name: '',
      last_name: '',
      email: '',
      phone: ''
    },
    deal_info: {
      name: '',
      amount: '',
      deal_type_id: '',
      status_id: '',
      description: '',
      expected_close_date: ''
    },
    additional_persons: [] as Array<{name: string, email: string, phone: string}>,
    convert_tasks: true
  })

  // Fetch data
  useEffect(() => {
    fetchData()
  }, [])

  useEffect(() => {
    fetchLeads()
  }, [filters])

  const fetchData = async () => {
    try {
      setLoading(true)
      const [leadsRes, statusesRes, urgencyRes, dealStatusesRes, dealTypesRes, partnersRes] = await Promise.all([
        makeApiCall('/api/leads'),
        makeApiCall('/api/lead-settings/statuses'),
        makeApiCall('/api/lead-settings/urgency-levels'),
        makeApiCall('/api/deals/statuses'),
        makeApiCall('/api/settings/deal-types'),
        makeApiCall('/api/partners')
      ])

      const leadsResponse = leadsRes.ok ? await leadsRes.json() : { leads: [] }
      const statusesData = statusesRes.ok ? await statusesRes.json() : []
      const urgencyData = urgencyRes.ok ? await urgencyRes.json() : []
      const dealStatusesResponse = dealStatusesRes.ok ? await dealStatusesRes.json() : { statuses: [] }
      const dealTypesResponse = dealTypesRes.ok ? await dealTypesRes.json() : { deal_types: [] }
      const partnersResponse = partnersRes.ok ? await partnersRes.json() : { partners: [] }

      setLeads(leadsResponse.leads || [])
      setLeadStatuses(statusesData)
      setUrgencyLevels(urgencyData)
      setDealStatuses(dealStatusesResponse.statuses || [])
      setDealTypes(dealTypesResponse.deal_types || [])
      setPartners(partnersResponse.partners || [])

      // Set default status and urgency for new leads
      if (statusesData.length > 0 && !leadForm.status_id) {
        setLeadForm(prev => ({ ...prev, status_id: statusesData[0].id }))
      }
      if (urgencyData.length > 0 && !leadForm.urgency_id) {
        setLeadForm(prev => ({ ...prev, urgency_id: urgencyData[0].id }))
      }
    } catch (err) {
      console.error('Error fetching data:', err)
      setError('Failed to fetch data')
    } finally {
      setLoading(false)
    }
  }

  const fetchLeads = async () => {
    try {
      const params = new URLSearchParams()
      if (filters.status_id) params.append('status_id', filters.status_id)
      if (filters.urgency_id) params.append('urgency_id', filters.urgency_id)
      if (filters.search) params.append('search', filters.search)

      const response = await makeApiCall(`/api/leads?${params.toString()}`)
      if (response.ok) {
        const data = await response.json()
        setLeads(data.leads || [])
      }
    } catch (err) {
      console.error('Error fetching leads:', err)
    }
  }

  const handleAddLead = async () => {
    // Clear previous error
    setLeadFormError(null)
    
    // Validate at least one contact field
    if (!leadForm.first_name && !leadForm.last_name && !leadForm.email && !leadForm.phone) {
      setLeadFormError(t('leads.pleaseProvideContact'))
      return
    }

    setLeadFormLoading(true)
    
    try {
      if (addLeadModal.editingLead) {
        const response = await makeJsonApiCall(`/api/leads/${addLeadModal.editingLead.id}`, 'PUT', leadForm)
        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.message || t('leads.failedToSaveLead'))
        }
      } else {
        const response = await makeJsonApiCall('/api/leads', 'POST', leadForm)
        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.message || t('leads.failedToSaveLead'))
        }
      }

      setAddLeadModal({ isOpen: false })
      resetLeadForm()
      setPartnerSearch('')
      setShowPartnerDropdown(false)
      setLeadFormError(null)
      fetchLeads()
    } catch (err: any) {
      console.error('Error saving lead:', err)
      setLeadFormError(err.message || t('leads.failedToSaveLead'))
    } finally {
      setLeadFormLoading(false)
    }
  }

  const handleConvertLead = async () => {
    if (!convertLeadModal.lead) {
      console.error('No lead selected for conversion')
      return
    }

    try {
      const response = await makeJsonApiCall(`/api/leads/${convertLeadModal.lead.id}/convert`, 'POST', convertForm)
      
      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to convert lead')
      }

      setConvertLeadModal({ isOpen: false, lead: null })
      resetConvertForm()
      await fetchLeads()
      alert(t('leads.leadConvertedSuccessfully'))
    } catch (err: any) {
      console.error('Error converting lead:', err)
      if (err.response?.data?.missing_fields) {
        alert(`${t('leads.pleaseFillMissingFields')} ${Object.keys(err.response.data.missing_fields).filter(k => err.response.data.missing_fields[k]).join(', ')}`)
      } else if (err.message) {
        alert(`${t('leads.failedToConvertLead')}: ${err.message}`)
      } else {
        alert(t('leads.failedToConvertLead'))
      }
    }
  }

  const handleDeleteLead = async (leadId: number) => {
    if (!confirm(t('leads.areYouSureDelete'))) return

    try {
      const response = await makeApiCall(`/api/leads/${leadId}`, { method: 'DELETE' })
      if (!response.ok) {
        throw new Error('Failed to delete lead')
      }
      fetchLeads()
    } catch (err) {
      console.error('Error deleting lead:', err)
      alert(t('leads.failedToDeleteLead'))
    }
  }

  const openAddLeadModal = () => {
    resetLeadForm()
    setPartnerSearch('')
    setShowPartnerDropdown(false)
    setLeadFormError(null)
    setLeadFormLoading(false)
    setShowTaskSection(false)
    setAddLeadModal({ isOpen: true })
  }

  const openEditModal = (lead: Lead) => {
    setLeadForm({
      first_name: lead.first_name || '',
      last_name: lead.last_name || '',
      email: lead.email || '',
      phone: lead.phone || '',
      description: lead.description || '',
      status_id: lead.status_id.toString(),
      urgency_id: lead.urgency_id.toString(),
      partner_id: lead.partner_id?.toString() || '',
      task: {
        description: '',
        due_date: '',
        priority: 'medium'
      }
    })
    // Pre-fill the partner search field with the current partner name
    setPartnerSearch(lead.partner_name || '')
    setShowPartnerDropdown(false)
    setLeadFormError(null)
    setLeadFormLoading(false)
    setAddLeadModal({ isOpen: true, editingLead: lead })
  }

  const openConvertModal = (lead: Lead) => {
    setConvertForm(prev => ({
      ...prev,
      missing_info: {
        first_name: lead.first_name || '',
        last_name: lead.last_name || '',
        email: lead.email || '',
        phone: lead.phone || ''
      },
      deal_info: {
        ...prev.deal_info,
        name: `${lead.first_name || ''} ${lead.last_name || ''}`.trim() + ' - Deal'
      }
    }))
    setConvertLeadModal({ isOpen: true, lead })
  }

  const openFollowUpModal = (lead: Lead) => {
    setFollowUpModal({ isOpen: true, lead })
  }

  const openLeadDetailModal = (lead: Lead) => {
    setLeadDetailModal({ isOpen: true, lead })
  }

  const resetLeadForm = () => {
    setLeadForm({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      description: '',
      status_id: leadStatuses[0]?.id.toString() || '',
      urgency_id: urgencyLevels[0]?.id.toString() || '',
      partner_id: '',
      task: {
        description: '',
        due_date: '',
        priority: 'medium'
      }
    })
  }

  const resetConvertForm = () => {
    setConvertForm({
      missing_info: {
        first_name: '',
        last_name: '',
        email: '',
        phone: ''
      },
      deal_info: {
        name: '',
        amount: '',
        deal_type_id: '',
        status_id: '',
        description: '',
        expected_close_date: ''
      },
      additional_persons: [],
      convert_tasks: true
    })
  }

  // Partner handlers
  const handlePartnerSearch = (value: string) => {
    setPartnerSearch(value)
    setShowPartnerDropdown(true)
  }

  const handleSelectPartner = (partner: any) => {
    setLeadForm(prev => ({ ...prev, partner_id: partner.id.toString() }))
    setPartnerSearch(`${partner.first_name} ${partner.last_name}`)
    setShowPartnerDropdown(false)
  }

  const handleClearPartner = () => {
    setLeadForm(prev => ({ ...prev, partner_id: '' }))
    setPartnerSearch('')
  }

  const handleAddNewPartner = async () => {
    if (!newPartnerForm.first_name || !newPartnerForm.last_name) {
      alert(t('leads.partnerNameRequired'))
      return
    }

    try {
      const response = await makeJsonApiCall('/api/partners', 'POST', newPartnerForm)
      if (response.ok) {
        const data = await response.json()
        const newPartner = data.partner
        setPartners(prev => [...prev, newPartner])
        setLeadForm(prev => ({ ...prev, partner_id: newPartner.id.toString() }))
        setPartnerSearch(`${newPartner.first_name} ${newPartner.last_name}`)
        setShowAddPartnerModal(false)
        setNewPartnerForm({ first_name: '', last_name: '', email: '', phone: '', agency: '' })
      }
    } catch (err) {
      console.error('Error creating partner:', err)
      alert(t('leads.failedToCreatePartner'))
    }
  }

  const filteredPartners = partners.filter(p => 
    `${p.first_name} ${p.last_name}`.toLowerCase().includes(partnerSearch.toLowerCase()) ||
    p.email?.toLowerCase().includes(partnerSearch.toLowerCase())
  )

  const getDisplayName = (lead: Lead) => {
    const name = `${lead.first_name || ''} ${lead.last_name || ''}`.trim()
    if (name) return name
    if (lead.email) return lead.email
    if (lead.phone) return lead.phone
    return 'Unknown Lead'
  }

  // Translation functions for status and urgency values
  const translateStatusName = (statusName: string): string => {
    const statusMap: { [key: string]: string } = {
      'New': t('leads.statusNew'),
      'Called': t('leads.statusCalled'),
      'Emailed': t('leads.statusEmailed'),
      'Waiting': t('leads.statusWaiting'),
      'Qualified': t('leads.statusQualified'),
      'Not Interested': t('leads.statusNotInterested'),
      'new': t('leads.statusNew'),
      'called': t('leads.statusCalled'),
      'emailed': t('leads.statusEmailed'),
      'waiting': t('leads.statusWaiting'),
      'qualified': t('leads.statusQualified'),
      'not interested': t('leads.statusNotInterested'),
    }
    return statusMap[statusName] || statusName
  }

  const translateUrgencyName = (urgencyName: string): string => {
    const urgencyMap: { [key: string]: string } = {
      'Low': t('leads.urgencyLow'),
      'Medium': t('leads.urgencyMedium'),
      'High': t('leads.urgencyHigh'),
      'low': t('leads.urgencyLow'),
      'medium': t('leads.urgencyMedium'),
      'high': t('leads.urgencyHigh'),
    }
    return urgencyMap[urgencyName] || urgencyName
  }

  // Helper function to calculate days since lead creation
  const getDaysSinceCreation = (createdAt: string) => {
    const createdDate = new Date(createdAt)
    const currentDate = new Date()
    const timeDifference = currentDate.getTime() - createdDate.getTime()
    const daysDifference = Math.floor(timeDifference / (1000 * 3600 * 24))
    return daysDifference
  }

  // Helper function to make links clickable in description text (for cards)
  const renderDescriptionWithLinks = (text: string) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g
    const parts = text.split(urlRegex)
    
    return parts.map((part, index) => {
      if (urlRegex.test(part)) {
        return (
          <a
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-900 dark:text-gray-600 hover:text-blue-800 dark:hover:text-blue-300 underline"
            onClick={(e) => e.stopPropagation()} // Prevent opening lead detail modal
          >
            {part}
          </a>
        )
      }
      return part
    })
  }

  if (loading) {
    return (
      <PageContent>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      </PageContent>
    )
  }

  return (
    <PageContent>
      {/* Page Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              {t('leads.title')}
            </h1>
            <p className="text-gray-600 dark:text-gray-300">
              {t('leads.subtitle')}
            </p>
          </div>
          <div className="flex items-center space-x-3">
            {/* View Toggle Button */}
            {viewSettings && (
              <button
                onClick={toggleViewMode}
                className="flex items-center px-4 py-2 bg-gradient-to-r from-gray-800 to-indigo-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
                title={`Switch to ${viewSettings.viewMode === 'cards' ? 'table' : 'cards'} view`}
              >
                {viewSettings.viewMode === 'cards' ? (
                  <>
                    <svg className="mr-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                    </svg>
                    {t('leads.viewTable')}
                  </>
                ) : (
                  <>
                    <svg className="mr-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
                    </svg>
                    {t('leads.viewCards')}
                  </>
                )}
              </button>
            )}
            <button
              onClick={openAddLeadModal}
              className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-gray-800 to-gray-900 text-white font-medium rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2"
            >
              <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              {t('leads.addLead')}
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-8">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('leads.status')}</label>
              <select
                value={filters.status_id}
                onChange={(e) => setFilters(prev => ({ ...prev, status_id: e.target.value }))}
                className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 transition-colors"
              >
                <option value="">{t('leads.allStatuses')}</option>
                {leadStatuses.map(status => (
                  <option key={status.id} value={status.id}>{translateStatusName(status.name)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('leads.urgency')}</label>
              <select
                value={filters.urgency_id}
                onChange={(e) => setFilters(prev => ({ ...prev, urgency_id: e.target.value }))}
                className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 transition-colors"
              >
                <option value="">{t('leads.allUrgencyLevels')}</option>
                {urgencyLevels.map(urgency => (
                  <option key={urgency.id} value={urgency.id}>{translateUrgencyName(urgency.name)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('common.search')}</label>
              <input
                type="text"
                placeholder={t('leads.searchPlaceholder')}
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 transition-colors"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Leads Display - Conditional Rendering based on view mode */}
      {leads.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
          <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/20 rounded-xl flex items-center justify-center mx-auto mb-4">
            <svg className="h-8 w-8 text-gray-900 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">{t('leads.noLeadsFound')}</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {t('leads.getStarted')}
          </p>
          <button
            onClick={openAddLeadModal}
            className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-gray-800 to-gray-900 text-white font-medium rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
          >
            <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            {t('leads.addFirstLead')}
          </button>
        </div>
      ) : viewSettings && viewSettings.viewMode === 'table' ? (
        /* Table View */
        <LeadsTableView
          leads={leads}
          onLeadClick={openLeadDetailModal}
          onFollowUpClick={(lead) => setFollowUpModal({ isOpen: true, lead })}
          onConvertClick={(lead) => setConvertLeadModal({ isOpen: true, lead })}
          loading={loading}
        />
      ) : (
        /* Card View (Default) */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {leads.map((lead) => (
            <div 
              key={lead.id} 
              className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-lg transition-all duration-200 cursor-pointer"
              onClick={() => openLeadDetailModal(lead)}
            >
              {/* Lead Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-r from-gray-800 to-gray-900 rounded-xl flex items-center justify-center">
                    <span className="text-lg font-bold text-white">
                      {getDisplayName(lead).charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {getDisplayName(lead)}
                    </h3>
                    <div className="flex flex-wrap items-center gap-2 mt-1">
                      {/* Status and Urgency - Primary row */}
                      <div className="flex items-center space-x-2">
                        <span
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-white"
                          style={{ backgroundColor: lead.status_color }}
                        >
                          {translateStatusName(lead.status_name)}
                        </span>
                        <span
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-white"
                          style={{ backgroundColor: lead.urgency_color }}
                        >
                          {translateUrgencyName(lead.urgency_name)}
                        </span>
                      </div>
                      
                      {/* Days counter */}
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                        📅 {getDaysSinceCreation(lead.created_at)} {getDaysSinceCreation(lead.created_at) === 1 ? t('leads.day') : t('leads.days')}
                      </span>
                      
                      {/* Assignment status */}
                      {lead.assigned_to ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200">
                          👤 {lead.assigned_to_name}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                          {t('leads.unassigned')}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Lead Details */}
              <div className="space-y-2 mb-4">
                {lead.email && (
                  <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                    </svg>
                    {lead.email}
                  </div>
                )}
                {lead.phone && (
                  <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    {lead.phone}
                  </div>
                )}
                {lead.task_count > 0 && (
                  <div className="flex items-center text-sm text-gray-900 dark:text-gray-600">
                    <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    {lead.task_count} {lead.task_count > 1 ? t('leads.tasks') : t('leads.task')}
                  </div>
                )}
                {lead.best_time_to_contact && (
                  <div className="flex items-center text-sm text-gray-900 dark:text-gray-600">
                    <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {t('leads.bestContact')}: {lead.best_time_to_contact.charAt(0).toUpperCase() + lead.best_time_to_contact.slice(1)}
                  </div>
                )}
                {lead.description && (
                  <div className="text-sm text-gray-600 dark:text-gray-400 mt-2 line-clamp-2">
                    {renderDescriptionWithLinks(lead.description)}
                  </div>
                )}
              </div>

              {/* Lead Footer */}
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {t('leads.created')} {new Date(lead.created_at).toLocaleDateString()}
                </div>
                <div className="mt-2 text-xs text-gray-900 dark:text-gray-600 font-medium">
                  {t('leads.clickToViewDetails')}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Lead Modal */}
      {addLeadModal.isOpen && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setAddLeadModal({ isOpen: false })
          }}
        >
          <div className="bg-white dark:bg-gray-800 rounded-3xl w-full max-w-2xl shadow-2xl max-h-[95vh] overflow-hidden border border-gray-200 dark:border-gray-700">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-gray-800 to-gray-900 p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold mb-2">
                  {addLeadModal.editingLead ? t('leads.editLead') : t('leads.addNewLead')}
                </h3>
                  <p className="text-blue-100 text-sm">
                    {addLeadModal.editingLead ? t('leads.updateLeadInfo') : t('leads.captureNewLeadInfo')}
                  </p>
                </div>
                <button
                  onClick={() => setAddLeadModal({ isOpen: false })}
                  className="p-2 hover:bg-white/20 rounded-full transition-colors"
                  aria-label="Close modal"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              </div>
              
            {/* Modal Body */}
            <div className="p-8 overflow-y-auto max-h-[calc(95vh-120px)]">
              <form onSubmit={(e) => { e.preventDefault(); handleAddLead(); }} className="space-y-6">
                {/* Error Message */}
                {leadFormError && (
                  <div className="p-4 bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 rounded-r-lg">
                    <div className="flex items-start">
                      <svg className="w-5 h-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      <div>
                        <h3 className="text-sm font-semibold text-red-800 dark:text-red-200 mb-1">
                          {t('common.error')}
                        </h3>
                        <p className="text-sm text-red-700 dark:text-red-300">
                          {leadFormError}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Contact Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="group">
                    <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                      {t('leads.firstName')}
                    </label>
                    <input
                      type="text"
                      value={leadForm.first_name}
                      onChange={(e) => setLeadForm(prev => ({ ...prev, first_name: e.target.value }))}
                      className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-lg placeholder-gray-400 dark:placeholder-gray-500"
                      placeholder={t('leads.firstNamePlaceholder')}
                    />
                  </div>
                  <div className="group">
                    <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                      {t('leads.lastName')}
                    </label>
                    <input
                      type="text"
                      value={leadForm.last_name}
                      onChange={(e) => setLeadForm(prev => ({ ...prev, last_name: e.target.value }))}
                      className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-lg placeholder-gray-400 dark:placeholder-gray-500"
                      placeholder={t('leads.lastNamePlaceholder')}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="group">
                    <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center">
                      <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                      {t('leads.email')}
                    </label>
                    <input
                      type="email"
                      value={leadForm.email}
                      onChange={(e) => setLeadForm(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 text-lg placeholder-gray-400 dark:placeholder-gray-500"
                      placeholder={t('leads.emailPlaceholder')}
                    />
                  </div>
                  <div className="group">
                    <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center">
                      <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                      {t('leads.phone')}
                    </label>
                    <input
                      type="tel"
                      value={leadForm.phone}
                      onChange={(e) => setLeadForm(prev => ({ ...prev, phone: e.target.value }))}
                      className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 text-lg placeholder-gray-400 dark:placeholder-gray-500"
                      placeholder={t('leads.phonePlaceholder')}
                    />
                  </div>
                </div>

                <div className="group">
                  <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center">
                    <span className="w-2 h-2 bg-gray-500 rounded-full mr-2"></span>
                    {t('leads.description')}
                  </label>
                  <textarea
                    value={leadForm.description}
                    onChange={(e) => setLeadForm(prev => ({ ...prev, description: e.target.value }))}
                    rows={4}
                    className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-500 focus:border-gray-500 transition-all duration-200 text-lg placeholder-gray-400 dark:placeholder-gray-500 resize-none"
                    placeholder={t('leads.descriptionPlaceholder')}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="group">
                    <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center">
                      <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
                      {t('leads.status')}
                    </label>
                    <select
                      value={leadForm.status_id}
                      onChange={(e) => setLeadForm(prev => ({ ...prev, status_id: e.target.value }))}
                      className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200 text-lg"
                    >
                      {leadStatuses.map(status => (
                        <option key={status.id} value={status.id}>{translateStatusName(status.name)}</option>
                      ))}
                    </select>
                  </div>
                  <div className="group">
                    <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center">
                      <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                      {t('leads.urgency')}
                    </label>
                    <select
                      value={leadForm.urgency_id}
                      onChange={(e) => setLeadForm(prev => ({ ...prev, urgency_id: e.target.value }))}
                      className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 text-lg"
                    >
                      {urgencyLevels.map(urgency => (
                        <option key={urgency.id} value={urgency.id}>{translateUrgencyName(urgency.name)}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Partner/Source Field */}
                <div className="group relative" ref={partnerDropdownRef}>
                  <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center">
                    <span className="w-2 h-2 bg-indigo-500 rounded-full mr-2"></span>
                    {t('leads.source')}
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={partnerSearch}
                      onChange={(e) => handlePartnerSearch(e.target.value)}
                      onFocus={() => setShowPartnerDropdown(true)}
                      className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 text-lg placeholder-gray-400 dark:placeholder-gray-500"
                      placeholder={t('leads.searchPartner')}
                    />
                    {leadForm.partner_id && (
                      <button
                        type="button"
                        onClick={handleClearPartner}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      >
                        ✕
                      </button>
                    )}
                    {showPartnerDropdown && (
                      <div className="absolute z-10 w-full mt-2 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                        {filteredPartners.length > 0 ? (
                          filteredPartners.map(partner => (
                            <button
                              key={partner.id}
                              type="button"
                              onClick={() => handleSelectPartner(partner)}
                              className="w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                            >
                              <div className="font-medium text-gray-900 dark:text-white">
                                {partner.first_name} {partner.last_name}
                              </div>
                              {partner.email && (
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                  {partner.email}
                                </div>
                              )}
                              {partner.agency && (
                                <div className="text-xs text-gray-400 dark:text-gray-500">
                                  {partner.agency}
                                </div>
                              )}
                            </button>
                          ))
                        ) : (
                          <div className="p-4">
                            <p className="text-gray-500 dark:text-gray-400 text-sm mb-3">
                              {t('leads.noPartnersFound')}
                            </p>
                            <button
                              type="button"
                              onClick={() => {
                                setShowPartnerDropdown(false)
                                setShowAddPartnerModal(true)
                              }}
                              className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
                            >
                              {t('leads.createNewPartner')}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {!addLeadModal.editingLead && (
                  <>
                    {/* Optional Task Section - Collapsible */}
                    <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
                      <button
                        type="button"
                        onClick={() => setShowTaskSection(!showTaskSection)}
                        className="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
                      >
                        <div className="flex items-center">
                          <svg className="w-5 h-5 text-yellow-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                          </svg>
                          <div className="text-left">
                            <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                              {t('leads.addTaskOptional')}
                            </h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                              {t('leads.createFollowUpTask')}
                            </p>
                          </div>
                        </div>
                        <svg 
                          className={`w-6 h-6 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${showTaskSection ? 'transform rotate-180' : ''}`}
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      
                      {showTaskSection && (
                        <div className="space-y-4 mt-4">
                        <div className="group">
                          <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center">
                            <span className="w-2 h-2 bg-yellow-500 rounded-full mr-2"></span>
                            {t('leads.taskDescription')}
                          </label>
                          <input
                            type="text"
                            value={leadForm.task.description}
                            onChange={(e) => setLeadForm(prev => ({ ...prev, task: { ...prev.task, description: e.target.value } }))}
                            className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all duration-200 text-lg placeholder-gray-400 dark:placeholder-gray-500"
                            placeholder={t('leads.followUpWithLead')}
                          />
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="group">
                            <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center">
                              <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
                              {t('leads.dueDate')}
                            </label>
                            <input
                              type="date"
                              value={leadForm.task.due_date}
                              onChange={(e) => setLeadForm(prev => ({ ...prev, task: { ...prev.task, due_date: e.target.value } }))}
                              className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 text-lg"
                            />
                          </div>
                          <div className="group">
                            <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center">
                              <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                              {t('leads.priority')}
                            </label>
                            <select
                              value={leadForm.task.priority}
                              onChange={(e) => setLeadForm(prev => ({ ...prev, task: { ...prev.task, priority: e.target.value as 'low' | 'medium' | 'high' } }))}
                              className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 text-lg"
                            >
                              <option value="low">{t('leads.low')}</option>
                              <option value="medium">{t('leads.medium')}</option>
                              <option value="high">{t('leads.high')}</option>
                            </select>
                          </div>
                        </div>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* Form Actions */}
                <div className="flex gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={() => setAddLeadModal({ isOpen: false })}
                    disabled={leadFormLoading}
                    className="flex-1 px-6 py-3 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 font-semibold text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={leadFormLoading}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-gray-800 to-gray-900 text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center"
                  >
                    {leadFormLoading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {t('common.saving')}
                      </>
                    ) : (
                      addLeadModal.editingLead ? t('leads.update') : t('leads.createLead')
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Convert Lead Modal */}
      {convertLeadModal.isOpen && convertLeadModal.lead && dealTypes && dealStatuses && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setConvertLeadModal({ isOpen: false, lead: null })
          }}
        >
          <div className="bg-white dark:bg-gray-800 rounded-3xl w-full max-w-4xl shadow-2xl max-h-[95vh] overflow-hidden border border-gray-200 dark:border-gray-700">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-green-600 to-emerald-700 p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold mb-2">
                    {t('leads.convertLead')}
                  </h3>
                  <p className="text-green-100 text-sm">
                    {t('leads.convertingLead')}: {getDisplayName(convertLeadModal.lead)}
                  </p>
                </div>
                <button
                  onClick={() => setConvertLeadModal({ isOpen: false, lead: null })}
                  className="p-2 hover:bg-white/20 rounded-full transition-colors"
                  aria-label="Close modal"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Modal Body */}
            <div className="p-8 overflow-y-auto max-h-[calc(95vh-120px)]">
              <div className="space-y-6">
                {/* Missing Info Section */}
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-6 border border-blue-200 dark:border-blue-800">
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                    <svg className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    {t('leads.completeRequiredInfo')}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="group">
                      <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center">
                        <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                        {t('leads.firstName')} *
                      </label>
                      <input
                        type="text"
                        value={convertForm.missing_info.first_name}
                        onChange={(e) => setConvertForm(prev => ({ ...prev, missing_info: { ...prev.missing_info, first_name: e.target.value } }))}
                        className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-lg placeholder-gray-400 dark:placeholder-gray-500"
                        required
                      />
                    </div>
                    <div className="group">
                      <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center">
                        <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                        {t('leads.lastName')} *
                      </label>
                      <input
                        type="text"
                        value={convertForm.missing_info.last_name}
                        onChange={(e) => setConvertForm(prev => ({ ...prev, missing_info: { ...prev.missing_info, last_name: e.target.value } }))}
                        className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-lg placeholder-gray-400 dark:placeholder-gray-500"
                        required
                      />
                    </div>
                    <div className="group">
                      <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center">
                        <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                        {t('leads.email')} *
                      </label>
                      <input
                        type="email"
                        value={convertForm.missing_info.email}
                        onChange={(e) => setConvertForm(prev => ({ ...prev, missing_info: { ...prev.missing_info, email: e.target.value } }))}
                        className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 text-lg placeholder-gray-400 dark:placeholder-gray-500"
                        required
                      />
                    </div>
                    <div className="group">
                      <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center">
                        <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                        {t('leads.phone')} *
                      </label>
                      <input
                        type="tel"
                        value={convertForm.missing_info.phone}
                        onChange={(e) => setConvertForm(prev => ({ ...prev, missing_info: { ...prev.missing_info, phone: e.target.value } }))}
                        className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 text-lg placeholder-gray-400 dark:placeholder-gray-500"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Deal Info Section */}
                <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-6 border border-green-200 dark:border-green-800">
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                    <svg className="h-5 w-5 text-green-600 dark:text-green-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {t('leads.customizeDeal')}
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                    {t('leads.dealCreatedAutomatically')}
                  </p>
                  <div className="space-y-6">
                    <div className="group">
                      <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center">
                        <span className="w-2 h-2 bg-gray-800 rounded-full mr-2"></span>
                        {t('leads.dealName')}
                      </label>
                      <input
                        type="text"
                        value={convertForm.deal_info.name}
                        onChange={(e) => setConvertForm(prev => ({ ...prev, deal_info: { ...prev.deal_info, name: e.target.value } }))}
                        className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-800 focus:border-gray-800 transition-all duration-200 text-lg placeholder-gray-400 dark:placeholder-gray-500"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="group">
                        <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center">
                          <span className="w-2 h-2 bg-yellow-500 rounded-full mr-2"></span>
                          {t('leads.amount')}
                        </label>
                        <div className="relative">
                          <span className="absolute left-4 top-3.5 text-gray-500 dark:text-gray-400 text-lg font-semibold">$</span>
                          <input
                            type="number"
                            value={convertForm.deal_info.amount}
                            onChange={(e) => setConvertForm(prev => ({ ...prev, deal_info: { ...prev.deal_info, amount: e.target.value } }))}
                            className="w-full px-4 py-3 pl-8 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all duration-200 text-lg placeholder-gray-400 dark:placeholder-gray-500"
                          />
                        </div>
                      </div>
                      <div className="group">
                        <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center">
                          <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
                          {t('leads.expectedCloseDate')}
                        </label>
                        <input
                          type="date"
                          value={convertForm.deal_info.expected_close_date}
                          onChange={(e) => setConvertForm(prev => ({ ...prev, deal_info: { ...prev.deal_info, expected_close_date: e.target.value } }))}
                          className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 text-lg"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="group">
                        <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center">
                          <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
                          {t('leads.dealType')}
                        </label>
                        <select
                          value={convertForm.deal_info.deal_type_id}
                          onChange={(e) => setConvertForm(prev => ({ ...prev, deal_info: { ...prev.deal_info, deal_type_id: e.target.value } }))}
                          className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200 text-lg"
                        >
                          <option value="">{t('leads.selectDealType')}</option>
                          {(dealTypes || []).map(type => (
                            <option key={type.id} value={type.id}>{type.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="group">
                        <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center">
                          <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                          {t('leads.dealStatus')}
                        </label>
                        <select
                          value={convertForm.deal_info.status_id}
                          onChange={(e) => setConvertForm(prev => ({ ...prev, deal_info: { ...prev.deal_info, status_id: e.target.value } }))}
                          className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 text-lg"
                        >
                          <option value="">{t('leads.selectDealStatus')}</option>
                          {(dealStatuses || []).map(status => (
                            <option key={status.id} value={status.id}>{status.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Convert Tasks Option */}
                <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-6 border border-purple-200 dark:border-purple-800">
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                    <svg className="h-5 w-5 text-purple-600 dark:text-purple-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    {t('leads.taskManagement')}
                  </h4>
                  <label className="flex items-center cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={convertForm.convert_tasks}
                      onChange={(e) => setConvertForm(prev => ({ ...prev, convert_tasks: e.target.checked }))}
                      className="w-5 h-5 text-purple-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-purple-500 focus:ring-2"
                    />
                    <span className="ml-3 text-base font-medium text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">{t('leads.convertTasks')}</span>
                  </label>
                </div>

                {/* Form Actions */}
                <div className="flex gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={() => setConvertLeadModal({ isOpen: false, lead: null })}
                    className="flex-1 px-6 py-3 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 font-semibold text-lg"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="button"
                    onClick={handleConvertLead}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-700 text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
                  >
                    {t('leads.convertLead')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Follow-up Modal */}
      {followUpModal.isOpen && followUpModal.lead && (
        <FollowUpModal
          isOpen={followUpModal.isOpen}
          onClose={() => setFollowUpModal({ isOpen: false, lead: null })}
          leadId={followUpModal.lead.id}
          leadName={getDisplayName(followUpModal.lead)}
          onSuccess={() => {
            fetchLeads() // Refresh leads to update task count
          }}
        />
      )}

      {/* Lead Detail Modal */}
      {leadDetailModal.isOpen && leadDetailModal.lead && (
        <LeadDetailModal
          lead={leadDetailModal.lead}
          onClose={() => setLeadDetailModal({ isOpen: false, lead: null })}
          onEdit={(lead) => {
            setLeadDetailModal({ isOpen: false, lead: null })
            openEditModal(lead)
          }}
          onConvert={(lead) => {
            setLeadDetailModal({ isOpen: false, lead: null })
            openConvertModal(lead)
          }}
          onFollowUp={(lead) => {
            setLeadDetailModal({ isOpen: false, lead: null })
            openFollowUpModal(lead)
          }}
          onDelete={(leadId) => {
            setLeadDetailModal({ isOpen: false, lead: null })
            handleDeleteLead(leadId)
          }}
        />
      )}

      {/* Add Partner Modal */}
      {showAddPartnerModal && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowAddPartnerModal(false)
          }}
        >
          <div className="bg-white dark:bg-gray-800 rounded-3xl w-full max-w-lg shadow-2xl border border-gray-200 dark:border-gray-700">
            <div className="bg-gradient-to-r from-indigo-600 to-indigo-800 p-6 text-white rounded-t-3xl">
              <h3 className="text-2xl font-bold">{t('leads.createNewPartner')}</h3>
              <p className="text-indigo-100 text-sm mt-1">{t('leads.addPartnerDetails')}</p>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('leads.firstName')} *
                  </label>
                  <input
                    type="text"
                    value={newPartnerForm.first_name}
                    onChange={(e) => setNewPartnerForm(prev => ({ ...prev, first_name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('leads.lastName')} *
                  </label>
                  <input
                    type="text"
                    value={newPartnerForm.last_name}
                    onChange={(e) => setNewPartnerForm(prev => ({ ...prev, last_name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('leads.email')}
                </label>
                <input
                  type="email"
                  value={newPartnerForm.email}
                  onChange={(e) => setNewPartnerForm(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('leads.phone')}
                </label>
                <input
                  type="tel"
                  value={newPartnerForm.phone}
                  onChange={(e) => setNewPartnerForm(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('leads.agency')}
                </label>
                <input
                  type="text"
                  value={newPartnerForm.agency}
                  onChange={(e) => setNewPartnerForm(prev => ({ ...prev, agency: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddPartnerModal(false)
                    setNewPartnerForm({ first_name: '', last_name: '', email: '', phone: '', agency: '' })
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleAddNewPartner}
                  className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
                >
                  {t('leads.createPartner')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </PageContent>
  )
}

export default Leads